import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import {
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
} from '@sunrun/experience-ui-components';

type DashboardLayoutProps = {
  children: ReactNode;
};

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  return <Grid>{children}</Grid>;
};

type DashboardLayoutItemProps = {
  children: ReactNode;
  /** Horizontal span in grid column tracks (default 1). */
  blockColumns?: number;
  /** Vertical span in grid row tracks (default 1). */
  blockRows?: number;
};

const DashboardLayoutItem = ({
  children,
  blockColumns = 1,
  blockRows = 1,
}: DashboardLayoutItemProps) => {
  const colSpan = Math.max(1, Math.floor(blockColumns));
  const rowSpan = Math.max(1, Math.floor(blockRows));

  return (
    <GridItem $blockColumns={colSpan} $blockRows={rowSpan}>
      {children}
    </GridItem>
  );
};

const BLOCK_ROW_HEIGHT_PX = 53;
const BLOCK_ROW_GAP_PX = 16;

const Grid = styled.div`
  display: grid;
  gap: ${BLOCK_ROW_GAP_PX}px;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  grid-auto-rows: ${BLOCK_ROW_HEIGHT_PX}px;
  align-items: stretch;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${TABLET_BREAKPOINT + 1}px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const GridItem = styled.div<{
  $blockColumns: number;
  $blockRows: number;
}>`
  min-width: 0;
  min-height: 0;
  grid-column: span ${(p) => p.$blockColumns};
  grid-row: span ${(p) => p.$blockRows};
`;

export {
  DashboardLayout,
  DashboardLayoutItem,
  BLOCK_ROW_HEIGHT_PX,
  BLOCK_ROW_GAP_PX,
};
